import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { showError, showSuccess } from "../../helpers/toast";

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

API.interceptors.response.use(
  (response) => Promise.resolve(response),
  (error) => {
    // Display error message
    console.error(error.response?.data?.message || error.message);
    return Promise.reject(error);
  }
);

// Async thunk for fetching notifications
export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async ({ accessToken, queryParams = {} }, { rejectWithValue }) => {
    try {
      let url = "/notification/";

      // Enforce default pagination to avoid server falling back to 10 items
      const params = { ...queryParams };
      if (!params.limit) params.limit = 30;
      if (!params.page) params.page = 1;

      if (Object.keys(params).length > 0) {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            searchParams.append(key, value);
          }
        });
        url = `${url}?${searchParams.toString()}`;
      }

      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${accessToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.get(url, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch notifications"
      );
    }
  }
);

// Async thunk for creating a notification
export const createNotification = createAsyncThunk(
  "notifications/createNotification",
  async ({ message, userId, accessToken, is_admin, subject, htmlMailContent, sendToGamingBoardAdmins, sendToGamingBoardViewers, sendToGamingBoardReviewers, sendToCompanyAdmins }, { rejectWithValue, dispatch }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${accessToken}`,
          "Content-Type": "application/json",
        },
      };

      // 1. Create the notification
      const response = await API.post(
        "/notification/",
        { message, userId, is_admin, subject, htmlMailContent, sendToGamingBoardAdmins, sendToGamingBoardViewers, sendToGamingBoardReviewers, sendToCompanyAdmins },
        config
      );

      // Refresh notifications list to ensure we have correct IDs from server
      await dispatch(fetchNotifications({ accessToken }));

      // 2. Send email to the user
      // Note: We swallow error here to not fail the notification creation if email fails, 
      // but log it.
      try {
        console.log("📧 Attempting to send email to user:", userId);
        await API.post(
          "/users/sendMail",
          {
            userId: userId,
            subject: subject || "Notification",
            htmlMailContent: htmlMailContent || "<p>check your account your application statius is</p>",
            sendToGamingBoardAdmins,
            sendToGamingBoardViewers,
            sendToGamingBoardReviewers,
            sendToCompanyAdmins
          },
          config
        );
        console.log("✅ Email sent successfully to user:", userId);
      } catch (emailError) {
        console.error("❌ Failed to send notification email:", emailError);
      }

      showSuccess("Notification created successfully");
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.error || "Failed to create notification";
      showError(errorMsg);
      return rejectWithValue(
        error.response?.data?.message || "Failed to create notification"
      );
    }
  }
);

// Async thunk for updating a notification
export const updateNotification = createAsyncThunk(
  "notifications/updateNotification",
  async (
    { notificationId, message, read_status, accessToken },
    { rejectWithValue }
  ) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${accessToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.put(
        `/notification/${notificationId}`,
        { message, read_status },
        config
      );
      //   showSuccess('Notification updated successfully');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update notification"
      );
    }
  }
);

// Async thunk for deleting a notification
export const deleteNotification = createAsyncThunk(
  "notifications/deleteNotification",
  async ({ configdata }, { rejectWithValue }) => {
    console.log(configdata.notification_ids, "nnnnnnnnnnnnnnnnn");
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${configdata.accessToken}`,
          "Content-Type": "application/json",
        },
      };
      await API.delete(
        `/notification/${configdata.notification_ids}`,
        config
      );
      //   showSuccess('Notification deleted successfully');
      return configdata.notification_ids;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete notification"
      );
    }
  }
);

const initialState = {
  notifications: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 30,
  },
  unreadCount: 0,
};

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createNotification.pending, (state) => {
        state.loading = true;
      })
      .addCase(createNotification.fulfilled, (state, action) => {
        state.loading = false;
        // We rely on fetchNotifications to update the list to ensure correct IDs
        state.error = null;
      })
      .addCase(createNotification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateNotification.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.notifications.findIndex(
          (notification) => notification.id === action.payload.id
        );
        if (index !== -1) {
          state.notifications[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = state.notifications.filter(
          (notification) => notification.id !== action.payload
        );
        state.error = null;
      })
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;

        // Debug: Log the server response
        console.log("🔍 Server Response:", action.payload);

        // Get requested pagination params
        const { page = 1, limit = 30 } = action.meta.arg.queryParams || {};

        // Handle both array response and paginated response
        if (Array.isArray(action.payload)) {
          // Server returned all notifications as array - this means server doesn't support pagination
          // We need to implement client-side pagination
          console.log("🔍 Server returned all notifications as array - implementing client-side pagination");

          const totalItems = action.payload.length;
          const totalPages = Math.ceil(totalItems / limit);
          const startIndex = (page - 1) * limit;
          const endIndex = startIndex + limit;

          // Slice the array to match the requested page
          state.notifications = action.payload.slice(startIndex, endIndex);

          state.pagination = {
            currentPage: Number(page),
            totalPages: totalPages || 1,
            totalItems: totalItems,
            itemsPerPage: Number(limit),
          };
        } else {
          // Handle server-side paginated response
          state.notifications = action.payload.data || [];
          state.unreadCount = action.payload.unreadCount || 0;

          // Update pagination metadata if available
          if (action.payload.pagination) {
            state.pagination = {
              currentPage: action.payload.pagination.page || 1,
              totalPages: action.payload.pagination.totalPages || 1,
              totalItems: action.payload.pagination.total || 0,
              itemsPerPage: action.payload.pagination.limit || limit || 30,
            };
          } else {
            // Fallback for object response without explicit pagination meta
            state.pagination = {
              currentPage: 1,
              totalPages: 1,
              totalItems: state.notifications.length,
              itemsPerPage: state.notifications.length,
            };
          }
        }

        // Debug: Log the final pagination state
        console.log("🔍 Final Pagination State:", state.pagination);

        state.error = null;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default notificationSlice.reducer;
